import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link } from '@zoom/react-ui';
import useVideoSettings from '../hooks/useVideoSettings';
import ToastManager from './ToastManager/ToastManager';

export default function VideoSettings({
  showError,
  watchType,
  showBanner,
  activeToasts,
  setActiveToasts,
}) {
  const clientSupportVideoSettings = useVideoSettings();
  const [isVideoMirrored, setIsVideoMirrored] = useState();
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    const updateVideoSettingsState = (videoSettingsChangeEvent) => {
      setIsVideoMirrored(videoSettingsChangeEvent?.mirrorMyVideo);
    };
    zoomSdk.addEventListener('onVideoSettingsChange', updateVideoSettingsState);
    return () => {
      zoomSdk.removeEventListener(
        'onVideoSettingsChange',
        updateVideoSettingsState
      );
    };
  }, []);

  useEffect(() => {
    if (clientSupportVideoSettings) {
      zoomSdk.callZoomApi('getVideoSettings').then((result) => {
        setIsVideoMirrored(result.mirrorMyVideo);
      });
    }
  }, [clientSupportVideoSettings]);

  const turnOnVideoAndTurnOffMirror = useCallback(() => {
    if (clientSupportVideoSettings) {
      turnOnVideo();
      setMirrorMyVideoOff();
    }
  }, [clientSupportVideoSettings]);

  const setMirrorMyVideoOff = useCallback(() => {
    if (clientSupportVideoSettings) {
      zoomSdk
        .callZoomApi('setVideoSettings', { mirrorMyVideo: false })
        .catch((error) => {
          // perhaps show error to user in future
        });
    }
  }, [clientSupportVideoSettings]);

  const turnOnVideo = useCallback(() => {
    if (clientSupportVideoSettings) {
      zoomSdk.callZoomApi('setVideoState', { video: true }).catch((error) => {
        // perhaps show error to user in future
      });
    }
  }, [clientSupportVideoSettings]);

  const toastsData = useMemo(
    () => [
      {
        toastId: 'turnOnVideo',
        type: 'error',
        condition: showError && !isVideoMirrored,
        content: (
          <>
            <Link
              className={`${
                clientSupportVideoSettings
                  ? 'toast-link'
                  : 'toast-link unsupported'
              }`}
              component={`${clientSupportVideoSettings ? 'button' : 'span'}`}
              onClick={turnOnVideo}
            >
              Start Video
            </Link>
            to show {watchType} in your video
          </>
        ),
        className: 'notification_toast_container toast-video-error',
        closable: true,
        duration: 0,
      },
      {
        toastId: 'turnOnVideoAndTurnOffMirror',
        type: 'error',
        condition: showError && isVideoMirrored,
        content: (
          <>
            <Link
              className={`${
                clientSupportVideoSettings
                  ? 'toast-link'
                  : 'toast-link unsupported'
              }`}
              component={`${clientSupportVideoSettings ? 'button' : 'span'}`}
              onClick={turnOnVideoAndTurnOffMirror}
            >
              Start video, turn off mirroring
            </Link>
            to show {watchType} in your video
          </>
        ),
        className: 'notification_toast_container toast-video-error',
        closable: true,
        duration: 0,
      },
      {
        toastId: 'setMirrorMyVideoOff',
        type: 'error',
        condition: !showError && isVideoMirrored,
        content: (
          <>
            <Link
              className={`${
                clientSupportVideoSettings
                  ? 'toast-link'
                  : 'toast-link unsupported'
              }`}
              component={`${clientSupportVideoSettings ? 'button' : 'span'}`}
              onClick={setMirrorMyVideoOff}
            >
              Turn off video mirroring
            </Link>
            to view {watchType} in right direction in your video
          </>
        ),
        className: 'notification_toast_container toast-video-error',
        closable: true,
        duration: 0,
      },
    ],
    [clientSupportVideoSettings, showError, isVideoMirrored]
  );

  useEffect(() => {
    if (
      clientSupportVideoSettings !== undefined &&
      isVideoMirrored !== undefined
    ) {
      setSettingsLoaded(true);
    }
  }, [clientSupportVideoSettings, isVideoMirrored]);

  return (
    <>
      {settingsLoaded && showBanner && showError && !isVideoMirrored && (
        <ToastManager
          toastsData={toastsData}
          dependencies={[showError, isVideoMirrored]}
          activeToasts={activeToasts}
          setActiveToasts={setActiveToasts}
        />
      )}
      {settingsLoaded && showBanner && !showError && isVideoMirrored && (
        <ToastManager
          toastsData={toastsData}
          dependencies={[showError, isVideoMirrored]}
          activeToasts={activeToasts}
          setActiveToasts={setActiveToasts}
        />
      )}
      {settingsLoaded && showBanner && showError && isVideoMirrored && (
        <ToastManager
          toastsData={toastsData}
          dependencies={[showError, isVideoMirrored]}
          activeToasts={activeToasts}
          setActiveToasts={setActiveToasts}
        />
      )}
    </>
  );
}
